import { useCallback, useEffect, useState } from 'react';
import { CrmApi } from '../../api/resources';
import { errorMessage, validationErrors } from '../../api/client';
import Modal from '../../components/Modal';
import ConfirmModal from '../../components/ConfirmModal';
import Icon from '../../components/Icon';

const TYPES = ['text', 'textarea', 'number', 'date', 'select', 'checkbox'];
const ENTITIES = [
  { value: 'lead', label: 'Lead registration' },
  { value: 'followup', label: 'Follow-up / status update' },
];

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^(\d)/, 'f_$1');

function FieldModal({ field, entity, onClose, onSaved }) {
  const editing = !!field;
  const [form, setForm] = useState({
    label: field?.label || '',
    key: field?.key || '',
    type: field?.type || 'text',
    optionsText: (field?.options || []).join('\n'),
    is_required: field?.is_required || false,
    is_active: field?.is_active ?? true,
  });
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setBanner('');
    const payload = {
      label: form.label,
      type: form.type,
      options:
        form.type === 'select'
          ? form.optionsText.split('\n').map((o) => o.trim()).filter(Boolean)
          : null,
      is_required: form.is_required,
      is_active: form.is_active,
    };
    try {
      if (editing) await CrmApi.customFields.update(field.id, payload);
      else await CrmApi.customFields.create({ ...payload, entity, key: form.key || slug(form.label) });
      onSaved();
    } catch (err) {
      setErrors(validationErrors(err));
      setBanner(errorMessage(err, 'Could not save field.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `Edit ${field.label}` : 'New custom field'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}
      <form onSubmit={submit}>
        <div className="field">
          <label>Label</label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          {errors.label && <div className="err">{errors.label}</div>}
        </div>
        {!editing && (
          <div className="field">
            <label>Key (auto from label, cannot change later)</label>
            <input
              type="text"
              placeholder={slug(form.label)}
              value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value })}
            />
            {errors.key && <div className="err">{errors.key}</div>}
          </div>
        )}
        <div className="field">
          <label>Type</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {form.type === 'select' && (
          <div className="field">
            <label>Options (one per line)</label>
            <textarea
              rows={4}
              value={form.optionsText}
              onChange={(e) => setForm({ ...form, optionsText: e.target.value })}
            />
            {errors.options && <div className="err">{errors.options}</div>}
          </div>
        )}
        <div className="field">
          <label className="check">
            <input
              type="checkbox"
              checked={form.is_required}
              onChange={(e) => setForm({ ...form, is_required: e.target.checked })}
            />
            Required
          </label>
        </div>
        <div className="field">
          <label className="check">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active (shown on the form)
          </label>
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

export default function CustomFieldsPage() {
  const [entity, setEntity] = useState('lead');
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit'|'delete', field }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setFields(await CrmApi.customFields.list(entity));
    } catch (err) {
      setError(errorMessage(err, 'Could not load custom fields.'));
    } finally {
      setLoading(false);
    }
  }, [entity]);

  useEffect(() => {
    load();
  }, [load]);

  const afterSave = () => {
    setModal(null);
    load();
  };

  const doDelete = async () => {
    try {
      await CrmApi.customFields.remove(modal.field.id);
      setModal(null);
      load();
    } catch (err) {
      setModal(null);
      setError(errorMessage(err, 'Could not delete field.'));
    }
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Custom fields</h1>
          <p className="page-sub">
            Add your own fields to the lead form and the follow-up form. The built-in fields always stay.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>
          <Icon name="plus" size={16} />
          New field
        </button>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="card">
        <div className="card-head">
          <select className="select-sm" value={entity} onChange={(e) => setEntity(e.target.value)}>
            {ENTITIES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
        </div>
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Label</th>
                <th>Key</th>
                <th>Type</th>
                <th>Required</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && fields.map((f) => (
                <tr key={f.id}>
                  <td className="name">{f.label}</td>
                  <td><code>{f.key}</code></td>
                  <td>{f.type}{f.type === 'select' && f.options ? ` (${f.options.length})` : ''}</td>
                  <td>{f.is_required ? 'Yes' : 'No'}</td>
                  <td>
                    <span className={f.is_active ? 'badge badge-ok' : 'badge badge-plain'}>
                      {f.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="icon-btn" title="Edit" onClick={() => setModal({ mode: 'edit', field: f })}>
                        <Icon name="edit" />
                      </button>
                      <button className="icon-btn danger" title="Delete" onClick={() => setModal({ mode: 'delete', field: f })}>
                        <Icon name="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && fields.length === 0 && (
          <div className="empty">
            <strong>No custom fields yet</strong>
            Click “New field” to add one.
          </div>
        )}
      </div>

      {modal?.mode === 'create' && (
        <FieldModal entity={entity} onClose={() => setModal(null)} onSaved={afterSave} />
      )}
      {modal?.mode === 'edit' && (
        <FieldModal field={modal.field} entity={entity} onClose={() => setModal(null)} onSaved={afterSave} />
      )}
      {modal?.mode === 'delete' && (
        <ConfirmModal
          title="Delete field"
          message={`Delete “${modal.field.label}”? Values already saved are kept but hidden. Deactivating is usually better.`}
          onConfirm={doDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
